import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert, Badge, Box, Button, ColumnLayout, Container, Form, FormField, Header,
  Select, SpaceBetween, Spinner, StatusIndicator, Table,
} from "@cloudscape-design/components";
import { getAuthToken } from "./auth";
import { getAwsCreds, getDefaultRegion } from "./awsCredentials";
import { apiFetch } from "./apiFetch";
import { useDocumentTitle } from "./useDocumentTitle";

interface AccountOption {
  value: string; // accountid
  label: string;
  alias: string;
}

interface ExistingProject {
  documentId: string;
  name: string;
}

interface DiscoveredItem {
  tagValue: string;
  existingProjects: ExistingProject[];
}

interface DiscoverResult {
  account: { alias: string | null; accountid: string | null };
  totalDiscovered: number;
  items: DiscoveredItem[];
}

interface SingleSyncResult {
  project: { documentId: string; name: string; id: number };
  action: "created" | "existing";
  stacks: { imported: number; updated: number; errors: number; attached: number };
  workloads: {
    created: number;
    updated: number;
    results: Array<{ type: string; action: "created" | "updated"; name: string }>;
  };
  customProduct: any | null;
  message: string;
}

interface BatchItem {
  tagValue: string;
  status: "ok" | "error";
  result?: SingleSyncResult;
  error?: string;
}

interface BatchResult {
  account: { alias: string | null; accountid: string | null };
  totalDiscovered: number;
  synced: number;
  failed: number;
  items: BatchItem[];
  message: string;
}

/**
 * A table row combines the discovered item (always present once discovery runs) with the sync
 * result for that tagValue (present only after a sync covering it). Rows not included in a sync
 * keep `syncResult` undefined and render "—" in the result columns.
 */
interface Row {
  tagValue: string;
  existingProjects: ExistingProject[];
  syncResult?: BatchItem;
}

const customProductSummary = (cp: any): string => {
  if (cp && cp.product) {
    const discovered = cp.results?.totalDiscovered ?? 0;
    return `${cp.product.name}${discovered > 0 ? ` (${discovered} resource(s))` : ""}`;
  }
  return "none";
};

export default function SyncAllByIclProjectPage() {
  useDocumentTitle("Sync All By ICLProject");
  const [accountOptions, setAccountOptions] = useState<AccountOption[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<AccountOption | null>(null);

  const [discovering, setDiscovering] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [rows, setRows] = useState<Row[]>([]);
  const [selectedRows, setSelectedRows] = useState<Row[]>([]);
  const [discovered, setDiscovered] = useState(false);
  const [summary, setSummary] = useState<{ discovered: number; synced: number; failed: number } | null>(null);

  useEffect(() => {
    const token = getAuthToken();
    if (!token) return;
    apiFetch(`/accounts`)
      .then((r) => r.json())
      .then((json) => {
        const opts = (json.data ?? []).map((a: any) => ({
          value: a.accountid,
          label: `${a.Alias} (${a.accountid})`,
          alias: a.Alias,
        }));
        setAccountOptions(opts);
      })
      .catch(() => {});
  }, []);

  const resolveCreds = () => {
    if (!selectedAccount) {
      setError("Please select an account.");
      return null;
    }
    const creds = getAwsCreds(selectedAccount.alias);
    if (!creds) {
      setError(`No AWS credentials for account "${selectedAccount.alias}". Add credentials in Settings first.`);
      return null;
    }
    return creds;
  };

  const handleDiscover = async () => {
    setError(null);
    setSummary(null);
    setRows([]);
    setSelectedRows([]);
    setDiscovered(false);

    const creds = resolveCreds();
    if (!creds || !selectedAccount) return;

    setDiscovering(true);
    try {
      const res = await apiFetch(`/projects/discover-icl-projects`, {
        method: "POST",
        headers: {
          "x-aws-access-key-id": creds.accessKeyId,
          "x-aws-secret-access-key": creds.secretAccessKey,
          "x-aws-session-token": creds.sessionToken,
        },
        body: JSON.stringify({
          accountId: selectedAccount.value,
          region: getDefaultRegion(),
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message || `API error: ${res.status}`);
      }
      const json: DiscoverResult = await res.json();
      setRows(
        (json.items ?? []).map((it) => ({
          tagValue: it.tagValue,
          existingProjects: it.existingProjects ?? [],
        }))
      );
      setDiscovered(true);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setDiscovering(false);
    }
  };

  const handleSync = async () => {
    setError(null);

    const creds = resolveCreds();
    if (!creds || !selectedAccount) return;
    if (selectedRows.length === 0) return;

    const tagValues = selectedRows.map((r) => r.tagValue);

    setSyncing(true);
    try {
      const res = await apiFetch(`/projects/sync-from-aws-batch`, {
        method: "POST",
        headers: {
          "x-aws-access-key-id": creds.accessKeyId,
          "x-aws-secret-access-key": creds.secretAccessKey,
          "x-aws-session-token": creds.sessionToken,
        },
        body: JSON.stringify({
          accountId: selectedAccount.value,
          region: getDefaultRegion(),
          tagValues,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message || `API error: ${res.status}`);
      }
      const json: BatchResult = await res.json();

      // Merge per-item results back into existing rows, keyed by tagValue. Rows not synced keep
      // their syncResult undefined so the result columns continue to show "—".
      const byTag = new Map<string, BatchItem>();
      for (const item of json.items ?? []) byTag.set(item.tagValue, item);

      setRows((prev) =>
        prev.map((r) => {
          const synced = byTag.get(r.tagValue);
          return synced ? { ...r, syncResult: synced } : r;
        })
      );
      setSummary({
        discovered: rows.length,
        synced: json.synced,
        failed: json.failed,
      });
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSyncing(false);
    }
  };

  const selectAll = () => setSelectedRows(rows);
  const clearSelection = () => setSelectedRows([]);

  const existingProjectCell = (item: Row) => {
    if (item.existingProjects.length > 0) {
      const first = item.existingProjects[0];
      const extra = item.existingProjects.length - 1;
      return (
        <>
          <Link to={`/projects/${first.documentId}`}>{first.name}</Link>
          {extra > 0 ? ` (+${extra})` : ""}
        </>
      );
    }
    return <Badge color="grey">new</Badge>;
  };

  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h2"
            description="Discover every ICLProject tag value in a chosen AWS account, then select which ones to sync."
          >
            Sync All By ICLProject
          </Header>
        }
      >
        <SpaceBetween size="m">
          <Alert type="info" header="How this works">
            First <strong>discover</strong> every distinct ICLProject tag value used by any resource
            in the selected account. Each value is annotated with any project that already exists for
            it (matched globally by name across all accounts). Then <strong>select</strong> the values
            you want and sync just those: for each selected value the sync finds or creates a project,
            attaches discoverable CloudFormation stacks, and (when no stacks are found) creates a
            custom product from the tagged resources. A failure on one value does not stop the others.
          </Alert>

          <Form
            actions={
              <Button variant="primary" onClick={handleDiscover} loading={discovering} disabled={syncing}>
                Discover
              </Button>
            }
          >
            <FormField
              label="Account"
              description="The AWS account to discover ICLProject values in and scope projects to. Uses the credentials stored for this account."
            >
              <Select
                selectedOption={selectedAccount}
                onChange={({ detail }) => setSelectedAccount(detail.selectedOption as unknown as AccountOption)}
                options={accountOptions}
                placeholder="Select account"
                filteringType="auto"
              />
            </FormField>
          </Form>

          {error && <Alert type="error" header="Operation failed">{error}</Alert>}
          {discovering && (
            <Box textAlign="center">
              <Spinner /> Discovering ICLProject values…
            </Box>
          )}
        </SpaceBetween>
      </Container>

      {discovered && (
        <Container
          header={
            <Header
              variant="h2"
              counter={`(${rows.length})`}
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button onClick={selectAll} disabled={rows.length === 0 || syncing}>
                    Select all
                  </Button>
                  <Button onClick={clearSelection} disabled={selectedRows.length === 0 || syncing}>
                    Clear
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSync}
                    loading={syncing}
                    disabled={selectedRows.length === 0}
                  >
                    Sync selected
                  </Button>
                </SpaceBetween>
              }
              description="Select the ICLProject values to sync. No rows are selected by default."
            >
              Discovered ICLProject values
            </Header>
          }
        >
          <SpaceBetween size="m">
            {summary && (
              <ColumnLayout columns={3} variant="text-grid">
                <div key="discovered">
                  <Box variant="awsui-key-label">Discovered</Box>
                  <Box>{summary.discovered}</Box>
                </div>
                <div key="synced">
                  <Box variant="awsui-key-label">Synced</Box>
                  <Box>{summary.synced}</Box>
                </div>
                <div key="failed">
                  <Box variant="awsui-key-label">Failed</Box>
                  <Box>{summary.failed}</Box>
                </div>
              </ColumnLayout>
            )}

            <Table
              variant="embedded"
              selectionType="multi"
              selectedItems={selectedRows}
              onSelectionChange={({ detail }) => setSelectedRows(detail.selectedItems as Row[])}
              items={rows}
              trackBy="tagValue"
              empty={<Box textAlign="center">No ICLProject values discovered in this account.</Box>}
              columnDefinitions={[
                {
                  id: "tagValue",
                  header: "ICLProject",
                  cell: (item: Row) => item.tagValue,
                },
                {
                  id: "existingProject",
                  header: "Existing project",
                  cell: (item: Row) => existingProjectCell(item),
                },
                {
                  id: "status",
                  header: "Status",
                  cell: (item: Row) =>
                    item.syncResult ? (
                      item.syncResult.status === "ok" ? (
                        <StatusIndicator type="success">ok</StatusIndicator>
                      ) : (
                        <StatusIndicator type="error">error</StatusIndicator>
                      )
                    ) : (
                      "—"
                    ),
                },
                {
                  id: "action",
                  header: "Action",
                  cell: (item: Row) =>
                    item.syncResult?.result ? (
                      <Badge color={item.syncResult.result.action === "created" ? "green" : "blue"}>
                        {item.syncResult.result.action}
                      </Badge>
                    ) : (
                      "—"
                    ),
                },
                {
                  id: "stacks",
                  header: "Stacks attached",
                  cell: (item: Row) =>
                    item.syncResult?.result ? String(item.syncResult.result.stacks.attached) : "—",
                },
                {
                  id: "workloads",
                  header: "Workloads (created/updated)",
                  cell: (item: Row) =>
                    item.syncResult?.result
                      ? `${item.syncResult.result.workloads.created}/${item.syncResult.result.workloads.updated}`
                      : "—",
                },
                {
                  id: "customProduct",
                  header: "Custom product / error",
                  cell: (item: Row) => {
                    if (!item.syncResult) return "—";
                    if (item.syncResult.status === "error") {
                      return (
                        <StatusIndicator type="error">
                          {item.syncResult.error || "unknown error"}
                        </StatusIndicator>
                      );
                    }
                    return customProductSummary(item.syncResult.result?.customProduct);
                  },
                },
              ]}
            />

            {syncing && (
              <Box textAlign="center">
                <Spinner /> Syncing selected ICLProject values…
              </Box>
            )}
          </SpaceBetween>
        </Container>
      )}
    </SpaceBetween>
  );
}
